import React, { type PropsWithChildren, useContext, useEffect, useMemo, useRef } from 'react';

import { useAnalyticsEvents } from '@atlaskit/analytics-next/useAnalyticsEvents';
import { getDocument } from '@atlaskit/browser-apis';
import { fg } from '@atlaskit/platform-feature-flags/fg';
import { ANALYTICS_CHANNEL } from '@atlassian/conversation-assistant-instrumentation/constants/analytics';

import { firstChatOpenUFOState } from '../ufo-state-publisher/firstChatOpenUFOState';
import { ChatStartupTimingContext } from './ChatStartupTimingContext';

type Props = PropsWithChildren<{
	startBoundary: 'asyncEntryRender' | 'providerRender';
	product: string;
	surface: string;
	enabled?: boolean;
}>;

/** Keep this early boundary free of chat-store and full chat analytics-hook imports. */
export const ChatStartupTimingProvider = ({
	children,
	startBoundary,
	product,
	surface,
	enabled = true,
}: Props): React.JSX.Element => {
	const parent = useContext(ChatStartupTimingContext);
	const { createAnalyticsEvent } = useAnalyticsEvents();
	// A local snapshot survives descendant Suspense retries. No startup events fire during render.
	const timing = useMemo<React.ContextType<typeof ChatStartupTimingContext>>(() => {
		const doc = getDocument();
		if (parent || !enabled || !doc || typeof window === 'undefined') {
			return null;
		}
		return {
			startupId: crypto.randomUUID(),
			startedAt: performance.now(),
			startBoundary,
			product,
			surface,
			committedInitializationEnabled: fg('rovo_chat_initialize_after_commit'),
			initialConversationsFetchDeduplicationEnabled: fg(
				'rovo_chat_dedupe_initial_conversations_fetch',
			),
			isFirstChatOpen: !firstChatOpenUFOState.hasOpenedChatInPageSession,
			hiddenDuringStartup: doc.visibilityState === 'hidden',
			started: false,
			finished: false,
		};
	}, [enabled, parent, product, startBoundary, surface]);
	const active = useRef<typeof timing>(null);
	useEffect(() => {
		active.current = timing;
		const doc = getDocument();
		if (!timing || !doc) {
			return;
		}
		const emit = (action: 'started' | 'usable' | 'aborted', endedAt?: number) => {
			try {
				createAnalyticsEvent({
					eventType: 'operational',
					actionSubject: 'rovoChatStartup',
					action,
					source: 'AIMate',
					tags: ['atlaskit', 'aiMate'],
					attributes: {
						startupId: timing.startupId,
						startBoundary: timing.startBoundary,
						product: timing.product,
						surface: timing.surface,
						committedInitializationEnabled: timing.committedInitializationEnabled,
						initialConversationsFetchDeduplicationEnabled:
							timing.initialConversationsFetchDeduplicationEnabled,
						isFirstChatOpen: timing.isFirstChatOpen,
						hiddenDuringStartup: timing.hiddenDuringStartup,
						readinessDefinition: 'editorReadyAndChatOnline',
						...(endedAt !== undefined && { durationMs: Math.max(0, endedAt - timing.startedAt) }),
						...(action === 'aborted' && { reason: 'closedBeforeUsable' }),
					},
				})?.fire?.(ANALYTICS_CHANNEL);
			} catch {
				// Best-effort telemetry must not prevent chat from loading.
			}
		};
		if (!timing.started) {
			timing.started = true;
			emit('started');
		}
		const reportUsable = (readyAt: number) => {
			if (!timing.finished) {
				timing.finished = true;
				emit('usable', readyAt);
			}
		};
		const abandon = () => {
			if (!timing.finished) {
				timing.finished = true;
				emit('aborted', performance.now());
			}
		};
		timing.reportUsable = reportUsable;
		// The input's passive effect can run before the owning boundary's effect.
		if (timing.readyAt !== undefined) {
			reportUsable(timing.readyAt);
		}
		const onVisibilityChange = () => {
			if (!timing.finished && doc.visibilityState === 'hidden') {
				timing.hiddenDuringStartup = true;
			}
		};
		if (!timing.finished) {
			doc.addEventListener('visibilitychange', onVisibilityChange);
			window.addEventListener('pagehide', abandon);
		}
		return () => {
			doc.removeEventListener('visibilitychange', onVisibilityChange);
			window.removeEventListener('pagehide', abandon);
			active.current = null;
			// Strict Mode replays setup before this runs; a real close abandons the attempt.
			queueMicrotask(() => {
				if (active.current !== timing) {
					abandon();
				}
			});
		};
	}, [createAnalyticsEvent, timing]);

	// Preserve child mount identity when a bootstrap-only shell becomes visible chat.
	return (
		<ChatStartupTimingContext.Provider value={parent ?? timing}>
			{children}
		</ChatStartupTimingContext.Provider>
	);
};
